/**
 * BrandsPage - live data only. /api/v1/dashboard/brands.
 */
import React from "react";
import { tokensV2 as T } from "./tokens.v2";
import { Tile } from "./Tile.v2";
import { AreaChart } from "./AreaChart.v2";
import { Donut } from "./Donut.v2";
import { Leaderboard } from "./Leaderboard.v2";
import { PageGuide } from "./PageGuide.v2";
import { useDashboardPage, formatINR, formatNum } from "./useDashboardPage.v2";
import { rangeWordFor, granLabel as glab } from "./rangeWord.v2";
import { LoadingShell, EmptyError } from "./PageState.v2";

// Status pill — small visual cue for campaign status / brand state.
function Pill({ tone = "neutral", children }) {
  const toneStyles = {
    success: { bg: "#E6F8E6", color: "#3A8A3A", border: "#BFE3BF" },
    warn:    { bg: "#FFF4E0", color: "#B26B00", border: "#F0D6A0" },
    danger:  { bg: "#FDE3E3", color: "#A12626", border: "#F0BFBF" },
    info:    { bg: "#E6EEFB", color: "#2156A8", border: "#BFD0EE" },
    purple:  { bg: "#EDE6FB", color: "#5832A8", border: "#D0BFEE" },
    neutral: { bg: "#EEF1F6", color: "#5B6678", border: "#D5DAE2" },
  }[tone] || { bg: "#EEF1F6", color: "#5B6678", border: "#D5DAE2" };
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 999,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      backgroundColor: toneStyles.bg,
      color: toneStyles.color,
      border: `1px solid ${toneStyles.border}`,
    }}>{children}</span>
  );
}

const STATUS_TONE = {
  active:    "success",
  open:      "info",
  approved:  "purple",
  completed: "neutral",
  closed:    "neutral",
  pending:   "warn",
  rejected:  "danger",
};

export function BrandsPage({ range, rangeId }) {
  const { data, loading, error, reload } = useDashboardPage("brands", range, rangeId);
  if (loading) return <LoadingShell tiles={5} />;
  if (!data) return <EmptyError title="Couldn't load Brands" error={error} onRetry={reload} />;

  const h = data.hero || {};
  const isTillDate = data.range?.isTillDate;
  const word = rangeWordFor(rangeId);
  const asOfWord = isTillDate ? "lifetime" : `as of end of ${word}`;
  // 3 cards, each "active / current out of a total".
  const totalBrands   = h.totalBrands?.value || 0;
  const activeBrands  = h.activeBrands?.value || 0;
  const brandsPct     = totalBrands > 0 ? Math.round((activeBrands / totalBrands) * 100) : 0;
  const activeCamp    = data.activeCampaignsCount || 0;
  const totalCamp     = data.totalCampaignsCount || 0;
  const campPct       = totalCamp > 0 ? Math.round((activeCamp / totalCamp) * 100) : 0;
  const walletSpent   = h.brandSpentWindow?.value || 0;   // spent in the selected range
  const walletLoaded  = h.brandLoadedWindow?.value || 0;  // loaded in the selected range
  const tiles = [
    { label: "Active brands", icon: "A", variant: "muted-orange", value: formatNum(activeBrands),
      subValue: `of ${formatNum(totalBrands)} total brands · ${brandsPct}%` },
    { label: `Active campaigns · ${word}`, icon: "C", variant: "muted-violet", value: formatNum(activeCamp),
      subValue: `of ${formatNum(totalCamp)} campaigns · ${campPct}%` },
    { label: `Brand wallet spent · ${word}`, icon: "W", variant: "muted-lime", value: formatINR(walletSpent),
      subValue: `of ${formatINR(walletLoaded)} loaded · ${word}` },
  ];
  const dealMix = (data.deals || []).map(d => ({ label: d.label, value: d.value }));
  const dealTotal = dealMix.reduce((s, d) => s + d.value, 0);
  const trend = data.trend?.cumulativeCampaignsLast12Months || [];
  const latest = trend[trend.length - 1]?.value || 0;

  // Top brands leaderboard — show campaign mix (public/private/affiliate)
  // and active campaign count under each name. Right-side number = lifetime spend.
  const fullINR = (n) => "Rs " + Math.round(n || 0).toLocaleString("en-IN");
  const topBrandRows = (data.topBrands || []).map(b => {
    const mix = [];
    if (b.publicCampaigns > 0)    mix.push(`${formatNum(b.publicCampaigns)} public`);
    if (b.privateCampaigns > 0)   mix.push(`${formatNum(b.privateCampaigns)} private`);
    if (b.affiliateCampaigns > 0) mix.push(`${formatNum(b.affiliateCampaigns)} affiliate`);
    const otherCount = b.totalCampaigns - b.publicCampaigns - b.privateCampaigns - b.affiliateCampaigns;
    if (otherCount > 0) mix.push(`${formatNum(otherCount)} other`);

    const status = [];
    if (b.activeCampaigns > 0)    status.push(`${formatNum(b.activeCampaigns)} live`);
    if (b.completedCampaigns > 0) status.push(`${formatNum(b.completedCampaigns)} completed`);
    if (b.walletBalance > 0)      status.push(`wallet ${formatINR(b.walletBalance)}`);

    // Verified-tick + inactive badge appended to the brand name (avatar still
    // gets the plain name for initials).
    const nameSuffix = (
      <>
        {b.isVerified && <span title="Verified by Fancall" style={{ marginLeft: 6, color: "#3A8A3A", fontSize: 12 }}>✓</span>}
        {!b.isActive   && <span title="Brand is currently inactive" style={{ marginLeft: 6, fontSize: 10, color: "#B26B00", fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "#FFF4E0" }}>INACTIVE</span>}
      </>
    );

    return {
      name: b.name,
      nameSuffix,
      value: b.totalSpent,
      displayValue: formatINR(b.totalSpent),
      displayTitle: `Lifetime spent: ${fullINR(b.totalSpent)}\nCurrent wallet: ${fullINR(b.walletBalance)}`,
      subtitle:  mix.length > 0    ? `${formatNum(b.totalCampaigns)} campaigns — ${mix.join(' · ')}` : `${formatNum(b.totalCampaigns)} campaigns`,
      subtitle2: status.length > 0 ? status.join(' · ') : null,
    };
  });

  // Active campaigns table rows
  const activeCampaignList = data.activeCampaigns || [];

  return (
    <>
      <div style={{ marginBottom: 16, padding: "0 4px" }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: T.text.primary }}>Brands</div>
        <div style={{ fontSize: 12, color: T.text.muted, marginTop: 4 }}>Brand campaigns + deals · live from production · {word}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
        {tiles.map(t => <Tile key={t.label} {...t} />)}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: 14, marginBottom: 12, alignItems: "stretch" }}>
        {dealMix.length > 0 && (
          <Donut title="Creator deal status" subtitle={`Each creator hired in a brand campaign — 1 per creator · ${word}`} data={dealMix} format={formatNum} centerLabel="Creator spots" centerValue={formatNum(dealTotal)} />
        )}
        {trend.length > 0 && (() => {
          const gran = data.range?.granularity || 'month';
          const granLabel = glab(gran);
          const isLifetime = data.range?.isTillDate;
          const titleSuffix = isLifetime ? 'last 12 months' : word;
          return (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6, padding: "0 4px" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text.primary }}>Cumulative campaigns — {titleSuffix}</div>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, padding: "4px 10px", background: T.surface.card, border: "1px solid " + T.border.subtle, borderRadius: 999 }}>{granLabel}</span>
              </div>
              <AreaChart data={trend} format={formatNum} summaryLabel={trend[trend.length - 1]?.label} summaryValue={formatNum(latest)} summaryDelta="" />
            </div>
          );
        })()}
      </div>

      {topBrandRows.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Leaderboard
            title={`Top brands by spend · ${word}`}
            subtitle={`${asOfWord.charAt(0).toUpperCase() + asOfWord.slice(1)} · sourced from brand_wallet.spent · campaign mix shows public/private/affiliate split`}
            rows={topBrandRows}
            rightLabel="Total spent"
          />
        </div>
      )}

      {activeCampaignList.length > 0 && (
        <div style={{ marginBottom: 12, background: T.surface.card, borderRadius: 16, padding: 16, borderLeft: `4px solid ${T.brand.base}`, boxShadow: "0 1px 2px rgba(31,42,63,0.04)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: T.text.primary, letterSpacing: "-0.01em" }}>Active campaigns</div>
              <div style={{ fontSize: 12, color: T.text.muted, marginTop: 4 }}>Live now · status: active / open / approved · top {activeCampaignList.length} by start date</div>
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted }}>{formatNum(data.activeCampaignsCount)} total</span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {activeCampaignList.map(c => {
              const visibilityTone = c.type?.toLowerCase() === "private" ? "purple"
                                   : c.type?.toLowerCase() === "public"  ? "info"
                                   : "neutral";
              const statusTone     = STATUS_TONE[c.status?.toLowerCase()] || "neutral";
              const fulfillmentPct = c.enrolledCreators > 0
                ? Math.round((c.completedCreators / c.enrolledCreators) * 100)
                : 0;
              return (
                <div key={c.id} style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 8px",
                  borderRadius: 10,
                  borderBottom: `1px solid ${T.border.subtle}`,
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: T.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 320 }}>{c.title}</span>
                      {c.type          && <Pill tone={visibilityTone}>{c.type}</Pill>}
                      {c.campaignType  && c.campaignType !== c.type && <Pill tone="neutral">{c.campaignType}</Pill>}
                      <Pill tone={statusTone}>{c.status}</Pill>
                      {c.paymentStatus && c.paymentStatus !== "paid" && (
                        <Pill tone={c.paymentStatus === "pending" ? "warn" : "neutral"}>Payment {c.paymentStatus}</Pill>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: T.text.muted }}>
                      <strong style={{ color: T.text.secondary }}>{c.brandName}</strong>
                      {/* PRIVATE → show actual creator names; PUBLIC → show enrolled count */}
                      {c.type?.toLowerCase() === "private" && c.selectedCreatorCount > 0 && (
                        <> · with{' '}
                          <span title={c.selectedCreatorNames.join(', ')} style={{ color: T.text.secondary, fontWeight: 600 }}>
                            {c.selectedCreatorNames.slice(0, 3).join(', ')}
                            {c.selectedCreatorCount > 3 && ` +${formatNum(c.selectedCreatorCount - 3)} more`}
                          </span>
                          {' '}({formatNum(c.selectedCreatorCount)} {c.selectedCreatorCount === 1 ? 'creator' : 'creators'})
                        </>
                      )}
                      {c.type?.toLowerCase() === "private" && c.selectedCreatorCount === 0 && (
                        <> · no creators selected yet</>
                      )}
                      {c.type?.toLowerCase() !== "private" && c.enrolledCreators > 0 && (
                        <> · {formatNum(c.enrolledCreators)} {c.enrolledCreators === 1 ? 'creator' : 'creators'} enrolled
                          {c.completedCreators > 0 && ` · ${formatNum(c.completedCreators)} delivered (${fulfillmentPct}%)`}
                        </>
                      )}
                      {c.type?.toLowerCase() !== "private" && c.enrolledCreators === 0 && (
                        <> · no creators enrolled yet</>
                      )}
                      {c.totalViews > 0 && <> · {formatNum(c.totalViews)} views</>}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", fontSize: 12, color: T.text.muted, whiteSpace: "nowrap" }}>
                    {c.budget > 0 ? (
                      <>
                        <div title={`Spent ${fullINR(c.totalSpent)} of budgeted ${fullINR(c.budget)}`}>
                          {formatINR(c.totalSpent)} <span style={{ color: T.text.muted }}>/ {formatINR(c.budget)}</span>
                        </div>
                        <div style={{ fontSize: 11, marginTop: 2 }}>{c.utilisationPct}% utilised</div>
                      </>
                    ) : (
                      <div style={{ fontSize: 11, fontStyle: "italic", color: T.text.muted }}>no budget set</div>
                    )}
                  </div>
                  {c.budget > 0 ? (
                    <div style={{ width: 80, height: 6, background: T.border.subtle, borderRadius: 3, overflow: "hidden" }} title={`Budget utilisation: ${c.utilisationPct}%`}>
                      <div style={{
                        width: `${Math.min(100, c.utilisationPct)}%`,
                        height: "100%",
                        background: c.utilisationPct >= 90 ? T.status.warn : T.brand.base,
                      }} />
                    </div>
                  ) : (
                    <div style={{ width: 80 }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <PageGuide groups={BRANDS_GUIDE} />
    </>
  );
}

// Definitions for every Brands-page card (kept in sync with brandsHandler +
// queries/brands.queries.js). Brands = users role_id=4; test brands excluded.
const BRANDS_GUIDE = [
  {
    section: "Overview",
    rows: [
      { card: "Active brands", means: "Brands currently marked active, out of all brands.", rule: "brands is_active=true ÷ total brands (deleted + test brands excluded)." },
      { card: "Active campaigns · window", means: "Open campaigns out of all campaigns created in the range.", rule: "brand_campaigns status IN (active, open, approved) ÷ all brand_campaigns created in window." },
      { card: "Brand wallet spent · window", means: "How much brands spent in the selected period, out of how much they loaded in that period (all-time on Till date).", rule: "brand_transaction_history debits (campaign_payment / creator_withdraw / fees) vs deposits (wallet_deposit), scoped to the range." },
    ],
  },
  {
    section: "Deals & campaigns",
    rows: [
      { card: "Creator deal status", means: "Each creator hired in a brand campaign (one row per creator — a 'creator spot'), split by deal status: completed / awaiting creator / closed-no-delivery / etc.", rule: "campaign_selected_creators grouped by status, created in window." },
      { card: "Cumulative campaigns", means: "Running total of campaigns ever created, by the end of each time bucket.", rule: "brand_campaigns cumulative count at each bucket end (auto granularity per date pill)." },
      { card: "Active campaigns (table)", means: "The currently-live campaigns with enrolment, budget used, creators and views/clicks.", rule: "brand_campaigns status IN (active, open, approved). Budget used = total_spent ÷ budget; enrolled = distinct creators in campaign_enrollments; completed = campaign_selected_creators status=completed." },
    ],
  },
  {
    section: "Top brands",
    rows: [
      { card: "Top brands by spend", means: "Top 10 brands by money spent on creators in the window, with campaign mix and current wallet.", rule: "Spend = SUM(brand_transaction_history.amount) where is_debited=true, payment_status=success, type IN (campaign_payment, creator_withdraw, commercial_charges_fee, enrollment_entry_fee), in window, NaN-guarded. Wallet = live SUM(brand_wallet.balance)." },
    ],
  },
];
